import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { InventoryItemCard } from '../InventoryItemCard/InventoryItemCard';
import DocumentIcon from '../Icons/DocumentIcon';
import CheckIcon from '../Icons/CheckIcon';
import './InventoryItemsGrid.css';

const PAGE_SIZE = 24;

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="4.5" stroke="#9CA3AF" strokeWidth="1.5"/>
    <path d="M10.5 10.5L13.5 13.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'OK', label: 'Ok' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DIFFERENCE', label: 'Difference' },
];

const getDefaultStatusFilter = (inventoryStatus) =>
{
  if (inventoryStatus === 'IN_REVIEW')
  {
    return 'RESOLVED';
  }

  if (inventoryStatus === 'REJECTED')
  {
    return 'PENDING';
  }

  if (inventoryStatus === 'ACTIVE')
  {
    return 'ALL';
  }

  if (inventoryStatus === 'COMPLETED')
  {
    return 'ALL';
  }

  return 'ALL';
};

export const InventoryItemsGrid = ({
  items,
  loading,
  error,
  onMarkResolved,
  onEditActual,
  onResolveAll,
  readOnly,
  inventoryStatus,
}) =>
{
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const userChangedFilterRef = useRef(false);

  const normalizedInventoryStatus = inventoryStatus || null;

  const hasPending = (items || []).some(i => i.status === 'PENDING');

  useEffect(() =>
  {
    if (!normalizedInventoryStatus)
    {
      return;
    }

    if (userChangedFilterRef.current)
    {
      return;
    }

    const defaultFilter = getDefaultStatusFilter(normalizedInventoryStatus);
    setStatusFilter(defaultFilter);
  }, [normalizedInventoryStatus]);

  const filtered = useMemo(() =>
  {
    let list = items || [];

    if (statusFilter !== 'ALL')
    {
      list = list.filter(i => i.status === statusFilter);
    }

    if (search.trim())
    {
      const q = search.trim().toLowerCase();

      list = list.filter(i =>
        (i.itemId || '').toLowerCase().includes(q) ||
        (i.itemDesc || '').toLowerCase().includes(q) ||
        (i.barcode || '').toLowerCase().includes(q) ||
        (i.colorDesc || '').toLowerCase().includes(q) ||
        (i.sizeDesc || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [items, search, statusFilter]);

  useEffect(() =>
  {
    setVisibleCount(PAGE_SIZE);
  }, [search, statusFilter]);

  const loadMore = useCallback(() =>
  {
    setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filtered.length));
  }, [filtered.length]);

  useEffect(() =>
  {
    const el = sentinelRef.current;

    if (!el)
    {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) =>
      {
        if (entries[0].isIntersecting)
        {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [loadMore]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <div className="inv-items-grid-container">
      <div className="inv-items-toolbar">
        <span className="inv-items-count-label">
          <DocumentIcon width={18} height={18} color="#000000"/>
          Inventory Items ({filtered.length})
        </span>

        <div className="inv-items-search-wrapper inv-items-push-right">
          <span className="inv-items-search-icon">
            <SearchIcon/>
          </span>

          <input
            className="inv-items-search"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select
          className="inv-items-select"
          value={statusFilter}
          onChange={e =>
          {
            userChangedFilterRef.current = true;
            setStatusFilter(e.target.value);
          }}
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {!readOnly && hasPending && (
          <button className="inv-items-resolve-all-btn" onClick={onResolveAll}>
            <CheckIcon width={14} height={14} color="red"/>
            Resolve All
          </button>
        )}
      </div>

      {loading ? (
        <div className="inv-items-loading">Loading inventory items...</div>
      ) : error ? (
        <div className="inv-items-error">Error loading inventory items. Please try again.</div>
      ) : filtered.length === 0 ? (
        <div className="inv-items-empty">No items found.</div>
      ) : (
        <>
          <div className="inv-items-grid">
            {visible.map((item, idx) => (
              <InventoryItemCard
                key={item.itemId || idx}
                item={item}
                onMarkResolved={onMarkResolved}
                onEditActual={onEditActual}
                readOnly={readOnly}
              />
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="inv-items-loading">
              Loading more...
            </div>
          )}
        </>
      )}
    </div>
  );
};